<template>
  <div>
    <StepHeader
      :title="stepTitle"
      subtitle-blue-mobile
    />

    <StepContent
      width="lg"
    >
      <div class="contact-details-step">
        <AppInput
          v-for="(field, index) in fields"
          :key="index"
          :label="field.label"
          :value="field.value"
          :error="field.error"
          :name="field.name"
          :pattern="field.pattern"
          :type="field.type"
          v-data-layer
          :data-layer="`${field.name}_field`"
          @input="handleInput(field.name, $event)"
        />
        <div class="divider" />

        <TwilioPhoneVerification
          :title="stepTitle"
          :is-submitted="submitted"
          subtitle-blue-mobile
          phone-field="home_phone"
          @code-request-success="submitted = false"
          @input-change="submitted = false"
          @phone-verification-completed="handlePhoneVerificationCompleted()"
        />
        <div
          v-if="resubmitError"
          class="error-message"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="24px"
            height="24px">
            <path
              d="M0 0h24v24H0z"
              fill="none"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {{ resubmitError }}
        </div>
      </div>
    </StepContent>

    <StepFooter>
      <AppButtonContinue
        v-data-layer="handleDataLayer"
        @click="handleContinue()"
      >
        Submit
      </AppButtonContinue>

      <StepTooltip>
        Get access to your vehicle and financing options by creating your account. There is no obligation to continue with financing if you change your mind.
      </StepTooltip>
    </StepFooter>

    <AppPopupSubmit
      v-show="showSubmitPopup"
      :submitted="showSubmitPopup"
      @completed="redirectToThanks()"
    />
  </div>
</template>

<script>
import AppButtonContinue from '@/components/common/buttons/AppButtonContinue'
import AppInput from '../inputs/AppInput'
import AppPhoneInput from '@/components/common/inputs/AppPhoneInput'
import AppPopupSubmit from '@/components/common/popups/AppPopupSubmit'
import StepContent from '@/components/common/step/StepContent'
import StepFooter from '@/components/common/step/StepFooter'
import StepHeader from '@/components/common/step/StepHeader'
import StepTooltip from '@/components/common/step/StepTooltip'
import TwilioPhoneVerification from '@/components/common/twilio/TwilioPhoneVerification.vue'
import submit from '@/mixins/submit'
import isEmail from 'validator/lib/isEmail'
import DataLayer from '../../../../data-layer'

export default {
  // Name
  name: 'contact-details',

  // Components
  components: {
    AppPhoneInput,
    AppButtonContinue,
    AppInput,
    AppPopupSubmit,
    StepContent,
    StepFooter,
    StepHeader,
    StepTooltip,
    TwilioPhoneVerification
  },

  // Mixins
  mixins: [submit],

  // Data
  data () {
    return {
      datalayerStepName: 'email',
      submitted: false,
      errors: {
        isRequired: 'This field is required.',
        isRequiredPhone: 'Enter your phone number',
        minTwoChars: 'Please enter at least 2 characters.',
        invalidPhoneNumber: 'Invalid phone number.',
        invalidEmailAddress: 'Enter a valid email address.',
        englishEmail: 'Please use only valid English characters'
      },
      stepTitle: 'Create Your Account',
      phoneVerificationCompleted: false
    }
  },

  // Computed
  computed: {
    fields () {
      return [{
        label: 'Email Address',
        name: 'email',
        type: 'email',
        value: this.email,
        error: this.emailError
      }]
    },

    submittedApp () {
      return this.$store.state.application.submitted_app
    },

    email () {
      return this.$store.state.application.email
    },

    emailError () {
      if (!this.submitted) return
      if (!this.email) return this.errors.isRequired
      if (!this.isValidEmail(this.email)) return this.errors.invalidEmailAddress
      if (!this.isValidEnglishEmail(this.email)) return this.errors.englishEmail
    },

    phone () {
      return this.$store.state.application.home_phone
    },

    phoneError () {
      if (!this.submitted) return
      if (!this.phone) return this.errors.isRequiredPhone
      if (!this.isValidPhone(this.phone)) return this.errors.invalidPhoneNumber
    },

    stepIsValid () {
      return !this.emailError && this.phoneVerificationCompleted
    }
  },

  // Methods
  methods: {
    /**
     * Handle Input
     * Update the store
     */
    handleInput (field, value) {
      this.$store.updateApplication(field, value)
    },

    /**
     * Handle Continue
     * @emits next
     */
    async handleContinue () {
      // Trigger Phone Number Verification before data submission
      this.submitted = true

      if (!this.emailError && this.phoneVerificationCompleted) {
        await this.handlePhoneVerificationCompleted()
        return
      }

      this.$bus.$emit('verify-phone-number')
    },

    async handlePhoneVerificationCompleted () {
      this.phoneVerificationCompleted = true

      if (this.submittedApp) {
        DataLayer.identify({
          email: this.email
        })
        DataLayer.track({ event: 'gpa_app_submitted' })
        this.redirectToThanks()
        return
      }

      if (this.stepIsValid) {
        this.showSubmitPopup = true
        await this.submitApplication()
      }
    },

    /**
     * Validate the users phone number
     */
    isValidPhone (phone) {
      const phoneClean = phone.trim().replace(/ /g, '')
      return /^(\+?1\s?-?)?(\(\d{3}\)|\d{3})[\s-]?\d{3}[\s-]?\d{4}$/.test(phoneClean)
    },

    /**
     * Validate the users email address
     */
    isValidEmail (email) {
      return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)
    },

    /**
     * Validate that the users email does not contain non-english characters.
     */
    isValidEnglishEmail (email) {
      return isEmail(email, { allow_utf8_local_part: false })
    },

    handleDataLayer () {
      DataLayer.track({ event: 'gpa_step_completed' })
      DataLayer.identify({
        phone: this.phone
      })
    }
  }
}
</script>

<style scoped lang="scss">
.step-header {
  padding-bottom: 0;
  margin-bottom: 29px;

  @include breakpoint('tablet-portrait-up') {
    margin-bottom: 54px;
  }

  @include breakpoint('desktop-up') {
    margin-bottom: 80px;
  }
}

.step-content {
  align-items: flex-start;
}

.contact-details-step {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;

  @include breakpoint('tablet-landscape-up') {
    width: 573px;
  }

  .app-input {
    margin-bottom: 0;
  }

  .divider {
    border-top: 1px solid #DDDDDD;
    height: 0;
    margin-top: 26.5px;
    margin-bottom: 25.5px;
    width: 100%;
  }
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
  color: #721c24;
  padding: 10px 25px;
  display: flex;
  align-items: center;
  gap: 15px;
}

</style>
